import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '@/firebase';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '@/config';
import { t } from '@/i18n';

// Types
export type UserRole = 'superadmin' | 'admin' | 'agent';

export interface AuthUser {
  uid: string;
  email: string;
  displayName?: string;
  tenantId?: string;
  role?: UserRole;
}

export interface Tenant {
  id: string;
  name: string;
  domain: string;
  plan: 'free' | 'pro' | 'enterprise';
  createdAt: Date;
  ownerUid: string;
}

interface AuthContextType {
  user: AuthUser | null;
  tenant: Tenant | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, tenantName: string, domain?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

// API client
const api = axios.create({
  baseURL: API_BASE_URL
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Create context
const AuthContext = createContext<AuthContextType | null>(null);

// Auth provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          await syncUserProfile(firebaseUser);
        } catch (error) {
          console.error('Failed to sync user profile:', error);
          toast.error(t('toast.profileLoadFailed'));
        }
      } else {
        setUser(null);
        setTenant(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sync user profile with backend (falls back to Firebase-only if backend unavailable)
  async function syncUserProfile(firebaseUser: User) {
    try {
      // Force refresh token to pick up latest custom claims (e.g., tenantId)
      const token = await firebaseUser.getIdToken(true);
      const response = await api.post('/auth/login', {
        idToken: token
      });

      setUser(response.data.user);
      setTenant(response.data.tenant);
    } catch (error) {
      console.warn('Backend unavailable, using Firebase auth only:', error);
      // Fallback: use Firebase user directly — try to get role from token claims
      const tokenResult = await firebaseUser.getIdTokenResult();
      const tenantId = (tokenResult.claims.tenantId as string) || undefined;
      setUser({
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        displayName: firebaseUser.displayName || undefined,
        tenantId,
        role: tenantId ? 'admin' : 'agent', // best-effort fallback
      });
      setTenant(null);
    }
  }

  // Sign in with email/password
  async function signIn(email: string, password: string) {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await syncUserProfile(result.user);
      toast.success(t('toast.signInSuccess'));
    } catch (error: any) {
      console.error('Sign in error:', error);
      toast.error(error.message || t('toast.signInFailed'));
      throw error;
    }
  }

  // Sign up with email/password
  async function signUp(email: string, password: string, tenantName: string, domain?: string) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const token = await result.user.getIdToken();
      
      // Create tenant via API
      const response = await api.post('/auth/signup', {
        idToken: token,
        tenantName,
        domain
      });
      
      setUser(response.data.user);
      setTenant(response.data.tenant);
      toast.success(t('toast.signUpSuccess'));
    } catch (error: any) {
      console.error('Sign up error:', error);
      toast.error(error.message || t('toast.signUpFailed'));
      throw error;
    }
  }

  // Sign in with Google
  async function signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if user has existing tenant
      const token = await result.user.getIdToken();
      try {
        const response = await api.post('/auth/login', {
          idToken: token
        });
        
        setUser(response.data.user);
        setTenant(response.data.tenant);
        toast.success(t('toast.googleSignInSuccess'));
      } catch (error: any) {
        // User doesn't have tenant, need to create one
        if (error.response?.status === 401) {
          // Redirect to tenant setup
          setUser({
            uid: result.user.uid,
            email: result.user.email!,
            displayName: result.user.displayName || undefined
          });
          toast.success(t('toast.completeSetup'));
        } else {
          throw error;
        }
      }
    } catch (error: any) {
      console.error('Google sign in error:', error);
      toast.error(error.message || t('toast.googleSignInFailed'));
      throw error;
    }
  }

  // Reset password
  async function resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success(t('toast.resetEmailSent'));
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast.error(error.message || t('toast.resetEmailFailed'));
      throw error;
    }
  }

  // Sign out
  async function logout() {
    try {
      await signOut(auth);
      setUser(null);
      setTenant(null);
      toast.success(t('toast.signOutSuccess'));
    } catch (error: any) {
      console.error('Sign out error:', error);
      toast.error(t('toast.signOutFailed'));
    }
  }

  // Refresh user profile
  async function refreshProfile() {
    const currentUser = auth.currentUser;
    if (currentUser) {
      await syncUserProfile(currentUser);
    }
  }

  const value: AuthContextType = {
    user,
    tenant,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    resetPassword,
    logout,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default useAuth;